import { Bell, BellOff, Smartphone } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Card, CardContent } from '@/components/ui/card';

export function NotificationToggle() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    isIOSPWA,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  // iOS not in PWA mode - show install prompt
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS && !isIOSPWA && !isSupported) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/10">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-1">
                Notificações no iOS
              </h4>
              <p className="text-sm text-muted-foreground mb-3">
                Para receber notificações no iPhone/iPad, adicione o PlantID à tela inicial:
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Toque no botão de compartilhar <span className="inline-block w-4 h-4 align-text-bottom">⎙</span></li>
                <li>Selecione "Adicionar à Tela de Início"</li>
                <li>Abra o app da tela inicial</li>
                <li>Ative as notificações aqui</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Browser doesn't support push notifications
  if (!isSupported) {
    return (
      <Card className="border-muted bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <BellOff className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground">
                Notificações não disponíveis
              </h4>
              <p className="text-sm text-muted-foreground">
                Seu navegador não suporta notificações push
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Permission denied
  if (permission === 'denied') {
    return (
      <Card className="border-destructive/30 bg-destructive/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <BellOff className="h-5 w-5 text-destructive" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground">
                Notificações bloqueadas
              </h4>
              <p className="text-sm text-muted-foreground">
                Permita notificações nas configurações do seu navegador
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Bell className={`h-5 w-5 ${isSubscribed ? 'text-primary' : 'text-muted-foreground'}`} />
            <div>
              <h4 className="font-medium text-foreground">
                Lembretes de Cuidados
              </h4>
              <p className="text-sm text-muted-foreground">
                Receba notificações quando for hora de regar suas plantas
              </p>
            </div>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={loading}
          />
        </div>
      </CardContent>
    </Card>
  );
}
